import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, GitMerge, Loader2, Plus, X } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useConfirm } from "@/components/Confirm";
import { PayoutSettings } from "@/components/Payout";
import { FONTS, useSettings } from "@/context/SettingsContext";
import { api, apiError } from "@/lib/api";
import { useRemembered } from "@/lib/useRemembered";
import { cn } from "@/lib/utils";

const inputCls = "w-full h-10 rounded-lg border border-line bg-card px-3 text-[13.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

function Panel({ title, hint, children }) {
  return (
    <section className="panel panel-pad">
      <h2 className="font-display text-[18px] font-semibold">{title}</h2>
      {hint && <p className="text-[12.5px] text-muted2 mt-1">{hint}</p>}
      <div className="mt-5 space-y-4">{children}</div>
    </section>
  );
}

function ListEditor({ label, items, onChange }) {
  const [v, setV] = useState("");
  const add = () => {
    const x = v.trim();
    if (x && !items.some((i) => i.toLowerCase() === x.toLowerCase())) onChange([...items, x]);
    setV("");
  };
  return (
    <div>
      <div className="text-[12px] font-semibold mb-2">{label}</div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {items.map((i) => (
          <span key={i} className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1 rounded-full bg-surface border border-line text-[12.5px]">
            {i}
            <button type="button" aria-label={`Remove ${i}`} onClick={() => onChange(items.filter((x) => x !== i))} className="p-0.5 rounded-full text-muted2 hover:text-stock-out">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {!items.length && <span className="text-[12.5px] text-muted2">None yet</span>}
      </div>
      <div className="flex gap-2">
        <input
          className={cn(inputCls, "h-9")}
          value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={`Add to ${label.toLowerCase()}…`}
        />
        <Button type="button" variant="outline" size="sm" className="h-9" onClick={add}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function MergeSpellings() {
  const [field, setField] = useState("label");
  const [values, setValues] = useRemembered("values");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const load = () => api.get("/values").then(({ data }) => setValues(data)).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const opts = (values?.[field] || []).filter((v) => v.count);
  const merge = async () => {
    try {
      const { data } = await api.post("/values/rename", { field, old: from, new: to });
      toast.success(`${data.changed} track${data.changed === 1 ? "" : "s"} now say “${to}”`);
      setFrom("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  return (
    <div className="rounded-xl border border-line p-4 space-y-3">
      <div className="flex items-center gap-2 text-[13.5px] font-semibold">
        <GitMerge className="w-4 h-4 text-gold" /> Merge two spellings
      </div>
      <p className="text-[12.5px] text-muted2">Every track using the first spelling is changed to the second — e.g. “DRAYCITY RECORDS” → “Draycity Records”.</p>
      <div className="grid sm:grid-cols-[130px_1fr_1fr_auto] gap-2">
        <select className={cn(inputCls, "h-9")} value={field} onChange={(e) => setField(e.target.value)} aria-label="What to merge">
          <option value="label">Label</option>
          <option value="distributor">Distributor</option>
          <option value="artist">Artist</option>
        </select>
        <select className={cn(inputCls, "h-9")} value={from} onChange={(e) => setFrom(e.target.value)} aria-label="Change this spelling">
          <option value="">Change this…</option>
          {opts.map((o) => (
            <option key={o.value} value={o.value}>
              {o.value} ({o.count})
            </option>
          ))}
        </select>
        <input className={cn(inputCls, "h-9")} list="merge-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="…to this" aria-label="New spelling" />
        <datalist id="merge-to">
          {opts.map((o) => (
            <option key={o.value} value={o.value} />
          ))}
        </datalist>
        <Button variant="outline" className="h-9" disabled={!from || !to.trim() || from === to} onClick={merge}>
          Merge
        </Button>
      </div>
    </div>
  );
}

export default function Settings() {
  const { settings, setSettings } = useSettings();
  const confirm = useConfirm();
  const [form, setForm] = useState(settings);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "writers" ? "writers" : "general";

  useEffect(() => setForm(settings), [settings]);

  const save = async (patch) => {
    const next = { ...form, ...patch };
    setForm(next);
    setBusy(true);
    try {
      const { data } = await api.put("/settings", {
        publisher_name: next.publisher_name,
        publisher_pro: next.publisher_pro,
        publisher_ipi: next.publisher_ipi,
        brand_color: next.brand_color,
        font_family: next.font_family,
        dark_mode: next.dark_mode,
        lists: next.lists,
      });
      setSettings((s) => ({ ...s, ...data }));
      toast.success("Saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (
      !(await confirm({
        title: "Delete every track and writer in this workspace?",
        body: "Settings and team logins (except writer logins) stay. Download a backup from Import & export first — this can't be undone.",
        confirmText: "Delete everything",
        destructive: true,
      }))
    )
      return;
    try {
      await api.delete("/admin/reset");
      toast.success("Workspace emptied");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Layout title="Settings" subtitle={tab === "writers" ? "Writer payouts: your percentage, the minimum, and what writers can see" : "Your publishing company, lists and how the app looks"}>
      <div className="flex gap-5 border-b border-line mb-5" role="tablist">
        {[
          ["general", "General"],
          ["writers", "Writers"],
        ].map(([k, l]) => (
          <button
            key={k}
            role="tab"
            aria-selected={tab === k}
            onClick={() => setParams(k === "general" ? {} : { tab: k })}
            className={cn("pb-2.5 px-1 text-[13.5px] font-semibold border-b-2 -mb-px", tab === k ? "border-brand text-brand" : "border-transparent text-muted2 hover:text-ink")}
          >
            {l}
          </button>
        ))}
      </div>
      {tab === "writers" && <PayoutSettings />}
      <div className={cn("grid xl:grid-cols-2 gap-4 items-start", tab !== "general" && "hidden")}>
        <Panel title="Publisher" hint="Printed on registration filing sheets as the publisher of every work.">
          <label className="block">
            <span className="block text-[12px] font-semibold mb-1.5">Company name</span>
            <input className={inputCls} value={form.publisher_name} onChange={set("publisher_name")} />
          </label>
          <div className="grid grid-cols-[1fr_1.5fr] gap-3">
            <label className="block">
              <span className="block text-[12px] font-semibold mb-1.5">PRO</span>
              <select className={inputCls} value={form.publisher_pro} onChange={set("publisher_pro")}>
                {form.lists.pros.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-[12px] font-semibold mb-1.5">Publisher IPI #</span>
              <input className={cn(inputCls, "font-mono")} value={form.publisher_ipi} onChange={set("publisher_ipi")} />
            </label>
          </div>
          <Button onClick={() => save({})} disabled={busy} className="bg-brand hover:bg-brand-hover">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </Panel>

        <Panel title="Appearance" hint="Applies to everyone in this workspace.">
          <div>
            <div className="text-[12px] font-semibold mb-2">Accent colour</div>
            <div className="flex gap-2 flex-wrap">
              {form.swatches.map((c) => (
                <button
                  key={c}
                  aria-label={`Accent ${c}`}
                  aria-pressed={form.brand_color?.toUpperCase() === c.toUpperCase()}
                  onClick={() => save({ brand_color: c })}
                  className="w-9 h-9 rounded-full flex items-center justify-center ring-offset-2 ring-offset-card hover:ring-2 hover:ring-line"
                  style={{ background: c }}
                >
                  {form.brand_color?.toUpperCase() === c.toUpperCase() && <Check className="w-4 h-4 text-white" />}
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="block text-[12px] font-semibold mb-1.5">Font</span>
            <select className={inputCls} value={form.font_family} onChange={(e) => save({ font_family: e.target.value })}>
              {FONTS.map((f) => (
                <option key={f}>{f}</option>
              ))}
            </select>
          </label>
          <div className="flex items-center justify-between rounded-xl border border-line px-3.5 py-3">
            <div>
              <div className="text-[13.5px] font-medium">Dark mode</div>
              <p className="text-[12px] text-muted2">Easier on the eyes in the studio.</p>
            </div>
            <Switch checked={!!form.dark_mode} onCheckedChange={(v) => save({ dark_mode: v })} aria-label="Dark mode" />
          </div>
        </Panel>

        <Panel title="Dropdown lists" hint="What's offered when you fill in a track. New values from imports are added automatically.">
          <ListEditor label="Labels" items={form.lists.labels} onChange={(labels) => setForm((f) => ({ ...f, lists: { ...f.lists, labels } }))} />
          <ListEditor label="Distributors" items={form.lists.distributors} onChange={(distributors) => setForm((f) => ({ ...f, lists: { ...f.lists, distributors } }))} />
          <ListEditor label="PROs" items={form.lists.pros} onChange={(pros) => setForm((f) => ({ ...f, lists: { ...f.lists, pros } }))} />
          <Button onClick={() => save({})} disabled={busy} className="bg-brand hover:bg-brand-hover">
            Save lists
          </Button>
          <MergeSpellings />
        </Panel>

        <Panel title="Start over" hint="Empties this workspace so you can re-import from scratch.">
          <Button variant="outline" className="text-stock-out" onClick={reset}>
            Delete all tracks and writers
          </Button>
        </Panel>
      </div>
    </Layout>
  );
}
